import React from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, MapPin, Truck, Package, Clock, Save, RefreshCw } from "lucide-react";
import { adminApi } from "../../lib/api";

const TABS = [
  { k: "zones", label: "Zoner", icon: MapPin },
  { k: "hub", label: "HUB", icon: Package },
  { k: "vehicles", label: "Køretøjer", icon: Truck },
  { k: "windows", label: "Tidsvinduer", icon: Clock },
];

export default function LogisticsPanel() {
  const [tab, setTab] = React.useState("zones");
  return (
    <div data-testid="logistics-panel">
      <div className="flex gap-1 border-b border-brand mb-6 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`logistics-subtab-${t.k}`}
              className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap flex items-center gap-2 ${
                tab === t.k ? "border-brand-red text-brand-red" : "border-transparent text-brand-text2 hover:text-brand-dark"
              }`}
            >
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "zones" && <ZonesView />}
      {tab === "hub" && <HubView />}
      {tab === "vehicles" && <VehiclesView />}
      {tab === "windows" && <WindowsView />}
    </div>
  );
}

/* ---------------- Zones ---------------- */
function ZonesView() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/logistics/zones");
      setItems(data.items || []);
    } catch (e) {
      toast.error("Kunne ikke hente zoner");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const onDelete = async (id) => {
    if (!window.confirm("Slet denne zone?")) return;
    try {
      await adminApi().delete(`/admin/logistics/zones/${id}`);
      toast.success("Zone slettet");
      load();
    } catch { toast.error("Sletning fejlede"); }
  };

  const onReseed = async () => {
    if (!window.confirm("Geninstaller standard-zoner? Eksisterende zoner røres ikke.")) return;
    try {
      const { data } = await adminApi().post("/admin/logistics/zones/seed-defaults");
      toast.success(`${data.inserted} zoner tilføjet`);
      load();
    } catch { toast.error("Re-seed fejlede"); }
  };

  if (loading) return <div className="text-brand-text2"><Loader2 className="animate-spin inline mr-2" size={16} /> Indlæser zoner...</div>;

  return (
    <div className="space-y-4" data-testid="zones-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-display font-bold text-2xl text-brand-dark">Postnummer-zoner ({items.length})</h3>
        <div className="flex gap-2">
          <button onClick={onReseed} className="btn-secondary" data-testid="zones-reseed-btn">
            <RefreshCw size={16} /> Geninstaller standard
          </button>
          <button onClick={() => setEditing({ name: "", postal_codes: [], region: "", color: "#DC2626", active: true })} className="btn-primary" data-testid="zones-new-btn">
            <Plus size={16} /> Ny zone
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((z) => (
          <div key={z.id} className="bg-white border border-brand p-4" data-testid={`zone-card-${z.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-3 h-12 mt-1" style={{ background: z.color || "#999" }} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-brand-dark truncate">{z.name}</div>
                <div className="text-xs text-brand-text2">{z.region || "Ingen region"}</div>
                <div className="text-xs text-brand-text2 mt-1">{(z.postal_codes || []).length} postnumre</div>
                {!z.active && <span className="inline-block text-xs px-2 py-0.5 mt-1 bg-gray-100 text-gray-600">Inaktiv</span>}
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setEditing(z)} className="text-xs underline text-brand-dark/80" data-testid={`zone-edit-${z.id}`}>Rediger</button>
              <button onClick={() => onDelete(z.id)} className="text-xs underline text-red-600 ml-auto" data-testid={`zone-delete-${z.id}`}>Slet</button>
            </div>
          </div>
        ))}
      </div>

      {editing && <ZoneEditModal zone={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ZoneEditModal({ zone, onClose, onSaved }) {
  const isNew = !zone.id;
  const [name, setName] = React.useState(zone.name || "");
  const [region, setRegion] = React.useState(zone.region || "");
  const [color, setColor] = React.useState(zone.color || "#DC2626");
  const [active, setActive] = React.useState(zone.active !== false);
  const [postalText, setPostalText] = React.useState((zone.postal_codes || []).join(", "));
  const [busy, setBusy] = React.useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const postal_codes = postalText.split(/[,\s\n]+/).map((s) => s.trim()).filter(Boolean);
      const body = { name, region, color, active, postal_codes };
      if (isNew) {
        await adminApi().post("/admin/logistics/zones", body);
        toast.success("Zone oprettet");
      } else {
        await adminApi().patch(`/admin/logistics/zones/${zone.id}`, body);
        toast.success("Zone opdateret");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gemning fejlede");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="zone-edit-modal" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-bold text-xl text-brand-dark">{isNew ? "Ny zone" : "Rediger zone"}</h3>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="col-span-2"><label className="label-flat">Navn</label>
            <input className="input-flat" value={name} onChange={(e) => setName(e.target.value)} data-testid="zone-name-input" /></div>
          <div><label className="label-flat">Region</label>
            <input className="input-flat" value={region} onChange={(e) => setRegion(e.target.value)} data-testid="zone-region-input" /></div>
          <div><label className="label-flat">Farve</label>
            <input type="color" className="input-flat h-10" value={color} onChange={(e) => setColor(e.target.value)} data-testid="zone-color-input" /></div>
          <div className="col-span-2"><label className="label-flat">Postnumre (komma-separeret)</label>
            <textarea className="input-flat min-h-[120px] font-mono text-sm" value={postalText} onChange={(e) => setPostalText(e.target.value)} placeholder="2300, 2400, 2500..." data-testid="zone-postal-input" /></div>
          <div className="col-span-2 flex items-center gap-2">
            <input type="checkbox" id="zone-active" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="zone-active-input" />
            <label htmlFor="zone-active">Aktiv</label>
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="btn-secondary" data-testid="zone-cancel-btn">Annullér</button>
          <button onClick={save} disabled={busy || !name.trim()} className="btn-primary" data-testid="zone-save-btn">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Gem
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- HUB ---------------- */
function HubView() {
  const [hub, setHub] = React.useState({ name: "EMA Kurer HUB", address: "", postal_code: "", city: "", country: "Danmark", contact_phone: "", notes: "" });
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi().get("/admin/logistics/hub");
        if (data && data.address) setHub(data);
      } catch { /* not configured yet */ }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    if (!hub.address || !hub.postal_code || !hub.city) {
      toast.error("Adresse, postnummer og by er påkrævet");
      return;
    }
    setBusy(true);
    try {
      const { data } = await adminApi().put("/admin/logistics/hub", hub);
      setHub(data);
      toast.success(data.lat ? `HUB gemt (geokodet: ${data.lat.toFixed(4)}, ${data.lng.toFixed(4)})` : "HUB gemt (geokoding fejlede — sæt lat/lng manuelt hvis nødvendigt)");
    } catch (e) {
      toast.error("Kunne ikke gemme HUB");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="text-brand-text2"><Loader2 className="animate-spin inline mr-2" size={16} /> Indlæser HUB...</div>;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="hub-view">
      <h3 className="font-display font-bold text-2xl text-brand-dark">EMA HUB-konfiguration</h3>
      <p className="text-sm text-brand-text2">Dette er den centrale sorterings-adresse hvor pickup-ruter slutter og delivery-ruter starter.</p>
      <div className="bg-white border border-brand p-5 space-y-3">
        <div><label className="label-flat">Navn</label>
          <input className="input-flat" value={hub.name || ""} onChange={(e) => setHub({ ...hub, name: e.target.value })} data-testid="hub-name-input" /></div>
        <div><label className="label-flat">Adresse</label>
          <input className="input-flat" value={hub.address || ""} onChange={(e) => setHub({ ...hub, address: e.target.value })} data-testid="hub-address-input" placeholder="Industrivej 1" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-flat">Postnummer</label>
            <input className="input-flat" value={hub.postal_code || ""} onChange={(e) => setHub({ ...hub, postal_code: e.target.value })} data-testid="hub-postal-input" placeholder="2600" /></div>
          <div><label className="label-flat">By</label>
            <input className="input-flat" value={hub.city || ""} onChange={(e) => setHub({ ...hub, city: e.target.value })} data-testid="hub-city-input" placeholder="Glostrup" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-flat">Lat (valgfri)</label>
            <input className="input-flat" type="number" step="0.0001" value={hub.lat ?? ""} onChange={(e) => setHub({ ...hub, lat: e.target.value ? parseFloat(e.target.value) : null })} data-testid="hub-lat-input" /></div>
          <div><label className="label-flat">Lng (valgfri)</label>
            <input className="input-flat" type="number" step="0.0001" value={hub.lng ?? ""} onChange={(e) => setHub({ ...hub, lng: e.target.value ? parseFloat(e.target.value) : null })} data-testid="hub-lng-input" /></div>
        </div>
        <div><label className="label-flat">Telefon (valgfri)</label>
          <input className="input-flat" value={hub.contact_phone || ""} onChange={(e) => setHub({ ...hub, contact_phone: e.target.value })} data-testid="hub-phone-input" /></div>
        <div><label className="label-flat">Noter</label>
          <textarea className="input-flat min-h-[80px]" value={hub.notes || ""} onChange={(e) => setHub({ ...hub, notes: e.target.value })} data-testid="hub-notes-input" /></div>
        <button onClick={save} disabled={busy} className="btn-primary" data-testid="hub-save-btn">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Gem HUB
        </button>
      </div>
    </div>
  );
}

/* ---------------- Vehicles ---------------- */
function VehiclesView() {
  const [items, setItems] = React.useState([]);
  const [classes, setClasses] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: vs }, { data: cs }] = await Promise.all([
        adminApi().get("/admin/logistics/vehicles"),
        adminApi().get("/admin/logistics/vehicle-classes"),
      ]);
      setItems(vs.items || []);
      setClasses(cs.items || []);
    } catch { toast.error("Kunne ikke hente køretøjer"); }
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const onDelete = async (id) => {
    if (!window.confirm("Slet dette køretøj?")) return;
    try {
      await adminApi().delete(`/admin/logistics/vehicles/${id}`);
      toast.success("Køretøj slettet");
      load();
    } catch { toast.error("Sletning fejlede"); }
  };

  if (loading) return <div className="text-brand-text2"><Loader2 className="animate-spin inline mr-2" size={16} /> Indlæser køretøjer...</div>;

  return (
    <div className="space-y-4" data-testid="vehicles-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-display font-bold text-2xl text-brand-dark">Køretøjer ({items.length})</h3>
        <button onClick={() => setEditing({ name: "", vehicle_class: "normal_hatch", owner_type: "driver_private", volume_l: 750, fill_rate: 0.72, max_weight_kg: 280, max_parcels: 32, seats_foldable: true, can_short_route: true, can_long_route: true, can_pickup: true, can_combined: true, active: true })} className="btn-primary" data-testid="vehicles-new-btn">
          <Plus size={16} /> Nyt køretøj
        </button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-brand p-6 text-center text-brand-text2" data-testid="vehicles-empty">
          Ingen køretøjer endnu. Tilføj første køretøj ovenfor.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((v) => {
            const cls = classes.find((c) => c.key === v.vehicle_class);
            return (
              <div key={v.id} className="bg-white border border-brand p-4" data-testid={`vehicle-card-${v.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-brand-dark truncate">{v.name}</div>
                    <div className="text-xs text-brand-text2 mt-0.5">{cls?.label || v.vehicle_class}</div>
                    {v.license_plate && <div className="text-xs text-brand-text2 font-mono mt-1">{v.license_plate}</div>}
                  </div>
                  {!v.active && <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600">Inaktiv</span>}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <div className="text-brand-text2">Volumen:</div><div className="text-brand-dark">{v.volume_l} L</div>
                  <div className="text-brand-text2">Brugbar:</div><div className="text-brand-dark">{v.usable_volume_l} L ({Math.round((v.fill_rate || 0) * 100)}%)</div>
                  <div className="text-brand-text2">Max vægt:</div><div className="text-brand-dark">{v.max_weight_kg} kg</div>
                  <div className="text-brand-text2">Max parcels:</div><div className="text-brand-dark">{v.max_parcels}</div>
                  <div className="text-brand-text2">Type:</div><div className="text-brand-dark">{v.owner_type === "driver_private" ? "Privat" : v.owner_type === "ema" ? "EMA-bil" : "Vognmand"}</div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setEditing(v)} className="text-xs underline text-brand-dark/80" data-testid={`vehicle-edit-${v.id}`}>Rediger</button>
                  <button onClick={() => onDelete(v.id)} className="text-xs underline text-red-600 ml-auto" data-testid={`vehicle-delete-${v.id}`}>Slet</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && <VehicleEditModal vehicle={editing} classes={classes} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function VehicleEditModal({ vehicle, classes, onClose, onSaved }) {
  const isNew = !vehicle.id;
  const [v, setV] = React.useState({ ...vehicle });
  const [busy, setBusy] = React.useState(false);

  const setField = (k, val) => setV((prev) => ({ ...prev, [k]: val }));

  const applyClassDefaults = (key) => {
    const cls = classes.find((c) => c.key === key);
    if (!cls) return;
    setV((prev) => ({
      ...prev,
      vehicle_class: key,
      volume_l: cls.default_volume_l,
      fill_rate: cls.default_fill_rate,
      max_parcels: cls.default_max_parcels,
      max_weight_kg: cls.default_max_weight_kg,
    }));
  };

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        await adminApi().post("/admin/logistics/vehicles", v);
        toast.success("Køretøj oprettet");
      } else {
        await adminApi().patch(`/admin/logistics/vehicles/${v.id}`, v);
        toast.success("Køretøj opdateret");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gemning fejlede");
    } finally { setBusy(false); }
  };

  const usable = Math.round((v.volume_l || 0) * (v.fill_rate || 0) * 10) / 10;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="vehicle-edit-modal" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display font-bold text-xl text-brand-dark">{isNew ? "Nyt køretøj" : "Rediger køretøj"}</h3>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="col-span-2"><label className="label-flat">Navn / model</label>
            <input className="input-flat" value={v.name || ""} onChange={(e) => setField("name", e.target.value)} data-testid="vehicle-name-input" placeholder="VW Touran 2018" /></div>
          <div><label className="label-flat">Køretøjsklasse</label>
            <select className="input-flat" value={v.vehicle_class || ""} onChange={(e) => applyClassDefaults(e.target.value)} data-testid="vehicle-class-select">
              {classes.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select></div>
          <div><label className="label-flat">Nummerplade</label>
            <input className="input-flat" value={v.license_plate || ""} onChange={(e) => setField("license_plate", e.target.value)} data-testid="vehicle-plate-input" /></div>
          <div><label className="label-flat">Ejer-type</label>
            <select className="input-flat" value={v.owner_type || ""} onChange={(e) => setField("owner_type", e.target.value)} data-testid="vehicle-owner-select">
              <option value="driver_private">Privat (chauffør)</option>
              <option value="ema">EMA-bil</option>
              <option value="subcontractor">Vognmand</option>
            </select></div>
          <div><label className="label-flat">Volumen (L)</label>
            <input type="number" className="input-flat" value={v.volume_l || 0} onChange={(e) => setField("volume_l", parseFloat(e.target.value) || 0)} data-testid="vehicle-volume-input" /></div>
          <div><label className="label-flat">Fyldningsgrad (0-1)</label>
            <input type="number" step="0.01" min="0" max="1" className="input-flat" value={v.fill_rate || 0} onChange={(e) => setField("fill_rate", parseFloat(e.target.value) || 0)} data-testid="vehicle-fillrate-input" /></div>
          <div className="col-span-2 bg-brand-bg2 p-3 text-sm">
            <span className="text-brand-text2">Brugbar kapacitet:</span> <span className="font-semibold text-brand-dark" data-testid="vehicle-usable-display">{usable} L</span>
          </div>
          <div><label className="label-flat">Max vægt (kg)</label>
            <input type="number" className="input-flat" value={v.max_weight_kg || 0} onChange={(e) => setField("max_weight_kg", parseFloat(e.target.value) || 0)} data-testid="vehicle-maxweight-input" /></div>
          <div><label className="label-flat">Max antal parcels</label>
            <input type="number" className="input-flat" value={v.max_parcels || 0} onChange={(e) => setField("max_parcels", parseInt(e.target.value) || 0)} data-testid="vehicle-maxparcels-input" /></div>
          <div className="col-span-2 grid grid-cols-2 gap-2 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!v.seats_foldable} onChange={(e) => setField("seats_foldable", e.target.checked)} data-testid="vehicle-seats-input" /> Bagsæder kan slås ned</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!v.can_short_route} onChange={(e) => setField("can_short_route", e.target.checked)} data-testid="vehicle-short-input" /> Kort rute (2.5-4h)</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!v.can_long_route} onChange={(e) => setField("can_long_route", e.target.checked)} data-testid="vehicle-long-input" /> Lang rute (5-7h)</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!v.can_pickup} onChange={(e) => setField("can_pickup", e.target.checked)} data-testid="vehicle-pickup-input" /> Pickup-ruter</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!v.can_combined} onChange={(e) => setField("can_combined", e.target.checked)} data-testid="vehicle-combined-input" /> Kombineret rute</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={v.active !== false} onChange={(e) => setField("active", e.target.checked)} data-testid="vehicle-active-input" /> Aktiv</label>
          </div>
          <div className="col-span-2"><label className="label-flat">Noter</label>
            <textarea className="input-flat min-h-[60px]" value={v.notes || ""} onChange={(e) => setField("notes", e.target.value)} data-testid="vehicle-notes-input" /></div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onClose} className="btn-secondary" data-testid="vehicle-cancel-btn">Annullér</button>
          <button onClick={save} disabled={busy || !v.name?.trim()} className="btn-primary" data-testid="vehicle-save-btn">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Gem
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Service Windows ---------------- */
function WindowsView() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/logistics/service-windows");
      setItems(data.items || []);
    } catch { toast.error("Kunne ikke hente tidsvinduer"); }
    setLoading(false);
  }, []);

  React.useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-brand-text2"><Loader2 className="animate-spin inline mr-2" size={16} /> Indlæser tidsvinduer...</div>;

  return (
    <div className="space-y-4" data-testid="windows-view">
      <h3 className="font-display font-bold text-2xl text-brand-dark">Tidsvinduer for produkter</h3>
      <p className="text-sm text-brand-text2">Cutoff-tider, afhentnings-, sorterings- og leveringsvinduer for Same Day og Next Day Business.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((w) => <WindowCard key={w.id} window={w} onSaved={load} />)}
      </div>
    </div>
  );
}

function WindowCard({ window: w, onSaved }) {
  const [edit, setEdit] = React.useState({ ...w });
  const [busy, setBusy] = React.useState(false);

  const setF = (k, v) => setEdit((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        product_name: edit.product_name,
        cutoff_time: edit.cutoff_time,
        pickup_window_start: edit.pickup_window_start,
        pickup_window_end: edit.pickup_window_end,
        hub_sort_start: edit.hub_sort_start,
        hub_sort_end: edit.hub_sort_end,
        delivery_window_start: edit.delivery_window_start,
        delivery_window_end: edit.delivery_window_end,
        active: edit.active !== false,
      };
      await adminApi().put(`/admin/logistics/service-windows/${edit.id}`, body);
      toast.success("Tidsvindue gemt");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gemning fejlede");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-brand p-5" data-testid={`window-card-${edit.id}`}>
      <h4 className="font-semibold text-brand-dark">{edit.product_name}</h4>
      <div className="text-xs text-brand-text2 mb-3">{edit.id}</div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div><label className="label-flat">Cutoff</label>
          <input type="time" className="input-flat" value={edit.cutoff_time || ""} onChange={(e) => setF("cutoff_time", e.target.value)} data-testid={`${edit.id}-cutoff`} /></div>
        <div><label className="label-flat">Pickup start</label>
          <input type="time" className="input-flat" value={edit.pickup_window_start || ""} onChange={(e) => setF("pickup_window_start", e.target.value)} data-testid={`${edit.id}-pickup-start`} /></div>
        <div><label className="label-flat">Pickup slut</label>
          <input type="time" className="input-flat" value={edit.pickup_window_end || ""} onChange={(e) => setF("pickup_window_end", e.target.value)} data-testid={`${edit.id}-pickup-end`} /></div>
        <div><label className="label-flat">HUB sortering start</label>
          <input type="time" className="input-flat" value={edit.hub_sort_start || ""} onChange={(e) => setF("hub_sort_start", e.target.value)} data-testid={`${edit.id}-sort-start`} /></div>
        <div><label className="label-flat">HUB sortering slut</label>
          <input type="time" className="input-flat" value={edit.hub_sort_end || ""} onChange={(e) => setF("hub_sort_end", e.target.value)} data-testid={`${edit.id}-sort-end`} /></div>
        <div><label className="label-flat">Levering start</label>
          <input type="time" className="input-flat" value={edit.delivery_window_start || ""} onChange={(e) => setF("delivery_window_start", e.target.value)} data-testid={`${edit.id}-delivery-start`} /></div>
        <div><label className="label-flat">Levering slut</label>
          <input type="time" className="input-flat" value={edit.delivery_window_end || ""} onChange={(e) => setF("delivery_window_end", e.target.value)} data-testid={`${edit.id}-delivery-end`} /></div>
      </div>
      <div className="flex items-center justify-between mt-4">
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={edit.active !== false} onChange={(e) => setF("active", e.target.checked)} data-testid={`${edit.id}-active`} /> Aktiv</label>
        <button onClick={save} disabled={busy} className="btn-primary" data-testid={`${edit.id}-save`}>
          {busy ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Gem
        </button>
      </div>
    </div>
  );
}
